import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiScissors } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const inputClass = "w-full bg-[#252b3b] border border-slate-700 rounded-lg py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors [&:-webkit-autofill]:[-webkit-box-shadow:0_0_0_1000px_#252b3b_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:#fff]";

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please enter both email and password'); return; }
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || 'Login failed. Please try again.');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) return null;

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel — salon photo ── */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <img
          src="/salon-bg.jpg"
          alt="Gumma's Glam House"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900/70 via-stone-900/50 to-stone-800/60" />

        {/* Logo top-left */}
        <div className="absolute top-8 left-8 flex items-center gap-3 z-10">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg">
            <FiScissors className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-none tracking-wide">Gumma's</p>
            <p className="text-amber-400 text-xs tracking-[0.2em] uppercase">Glam House</p>
          </div>
        </div>

        {/* Hero text */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <h1 className="text-5xl xl:text-6xl font-bold text-white leading-tight mb-4">
            Where Beauty<br />
            Meets <span className="text-amber-400 italic font-serif">Elegance</span>
          </h1>
          <p className="text-stone-300 text-base xl:text-lg max-w-sm leading-relaxed mb-10">
            Manage your salon operations with a premium experience designed for beauty professionals.
          </p>

          {/* Stats */}
          <div className="flex items-center gap-8">
            {[
              { num: '500+', label: 'HAPPY CLIENTS' },
              { num: '50+',  label: 'SERVICES' },
              { num: '4.9',  label: 'RATING' },
            ].map(({ num, label }) => (
              <div key={label}>
                <p className="text-white text-2xl font-bold">{num}</p>
                <p className="text-stone-400 text-xs tracking-widest mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Logo badge bottom-left */}
        <div className="absolute bottom-8 left-8 z-10 flex items-center gap-3">
          <img src="/logo.png" alt="Gumma's Logo" className="w-16 h-16 rounded-full object-contain bg-white/10 p-1" />
          <div>
            <p className="text-white font-semibold text-sm">Gumma's Glam House</p>
            <p className="text-stone-400 text-xs">The Luxe Unisex Salon · Vinukonda</p>
          </div>
        </div>
      </div>

      {/* ── Right panel — login form ── */}
      <div className="flex-1 lg:w-[45%] bg-[#1a1f2e] flex flex-col items-center justify-center px-8 sm:px-12 xl:px-16">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
            <FiScissors className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-none">Gumma's</p>
            <p className="text-amber-400 text-xs tracking-[0.2em] uppercase">Glam House</p>
          </div>
        </div>

        <div className="w-full max-w-sm">
          <h2 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Georgia, serif' }}>
            Welcome back
          </h2>
          <p className="text-slate-400 text-sm mb-8">Sign in to your dashboard</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-6 text-sm text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm text-slate-300 mb-2">Email Address</label>
              <div className="relative">
                <FiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  placeholder="you@gummas.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  className={`${inputClass} pl-10 pr-4`}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-slate-300 mb-2">Password</label>
              <div className="relative">
                <FiLock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                  className={`${inputClass} pl-10 pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-700" />
            <span className="text-slate-600 text-xs tracking-widest uppercase">Dashboard</span>
            <div className="flex-1 h-px bg-slate-700" />
          </div>

          <p className="text-center text-xs text-slate-600">
            © {new Date().getFullYear()} Gumma's Glam House. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
